/* ---------------------------------------------------------------------------
   The construction record, as it is read.

   THREE CONSUMERS, which is why this is its own module rather than a block
   inside one screen: Construction Monitoring's site record
   (src/screens/site.ts), that application's period report over a whole
   department, and the FIELD record, which is where a pour is recorded at the
   pour. All three draw the same row, and a second copy of it would drift — one
   would gain the reviewer's name and another would not, or two would disagree
   about how a quantity prints.

   IT IS NO LONGER ON THE DESK BRIDGE RECORD. That was its first home, while
   there was one application; what was built on a structure is Construction
   Monitoring's subject now, and the record links across to it. The FIELD record
   keeps the card, because Construction Monitoring is a desk application with no
   field shell — see the note at that call site in src/screens/detail.ts.

   WHAT A ROW HAS TO SAY, in the order it says it:

     · what was done, in the library's words rather than its id;
     · where — the component where a model named one, the element otherwise,
       and "Not recorded" where neither, because a pour nobody located is still
       a pour that happened;
     · how much, with its unit, and nothing at all where nothing was measured;
     · when it was done, which is not when it was written;
     · whether anybody has checked it.

   THE STATE IS NOT DECORATION. Proposal 1 sells FWO a record CSI Bangkok
   reviews and verifies, and the whole value of that promise is that a reader
   can tell a verified entry from one that arrived an hour ago. So the chip is
   on every row, including the accepted ones, and the card leads with how many
   are still unchecked.

   QUANTITIES TOTAL PER ACTIVITY AND UNIT AND NEVER ACROSS EITHER. The server
   rolls them up that way (`summariseProgress`) and this prints them that way:
   one line per activity and unit. Cubic metres of concrete and tonnes of steel
   are both quantities, and adding them produces a figure that looks like
   progress and measures nothing; so does adding the excavation to the pour, or
   formwork erected to formwork struck (LC-72).
   --------------------------------------------------------------------------- */

import { h, ICONS, type Child } from '../core/dom.ts';
import { actions, can, whyNot, type State } from '../core/store.ts';
import * as api from '../core/api.ts';
import { button, cardHead, emptyState, refusalNote, revertOnRefusal, tag } from '../components/widgets.ts';
import { activityLabel } from '../data/activities.ts';
import { elementLabel } from '../data/elements.ts';
import { prettyDate } from '../data/derive.ts';
import { documentKindLabel } from '../data/documents.ts';
import { incidentLabel, isLostTime, plantText, tradesText } from '../data/siteReturn.ts';
import { weatherChip } from '../components/weather.ts';
import { trimNumber } from '../data/quantities.ts';
import type { ProgressEntry, ProgressState, ProgressSummary } from '../data/types.ts';
import { itemList, mayRaiseAny, testList } from './siteQuality.ts';

/** The three states, in the order they are moved through. */
const STATES: readonly ProgressState[] = ['draft', 'review', 'accepted'];

/** What each state is called on screen. */
const STATE_LABEL: Record<ProgressState, string> = {
  draft: 'Recorded',
  review: 'In review',
  accepted: 'Verified',
};

/**
 * Where each state sits on the shared monochrome ramp — see tag() in widgets.ts.
 *
 * `draft` is the LOUDEST, which is the opposite of how a workflow chip is
 * usually drawn and is deliberate: an unverified entry is the one a reader has
 * to notice, because it is the one that may yet be wrong. A verified entry is
 * the quiet, settled case.
 */
const STATE_RANK: Record<ProgressState, 1 | 2 | 3> = {
  draft: 3,
  review: 2,
  accepted: 1,
};

/** The chip, for a state this build may not know. */
export function progressStateTag(state: string): HTMLElement {
  const known = (STATES as readonly string[]).includes(state);
  /* A row written by a build with a state this one does not know is still a
     row. Printed as its own string rather than coerced to `draft`, which would
     tell a reviewer their acceptance had been undone. */
  if (!known) return tag(state, 2, 'A state this version of the application does not know.');
  const s = state as ProgressState;
  return tag(STATE_LABEL[s], STATE_RANK[s]);
}

/** How much, with its unit — or nothing at all where nothing was measured. */
export function progressQuantity(e: ProgressEntry): string {
  if (e.quantity === null || e.unit === null) return '';
  return `${trimNumber(e.quantity)} ${e.unit}`;
}

/**
 * Where on the structure, in the most precise vocabulary the entry carries.
 *
 * The component first because it is the more precise of the two — "Bent-2 Cap"
 * against "Piers" — then the element, then the honest refusal. Both are
 * optional and independent on the row, exactly as they are on a defect.
 */
export function progressWhere(e: ProgressEntry): string {
  if (e.component) return e.component;
  if (e.element) return elementLabel(e.element);
  return 'Not recorded';
}

/* --------------------------- one row --------------------------- */

export interface RowOptions {
  /** Whether this reader may move an entry along. Draws a picker if so, a chip if not. */
  mayReview: boolean;
  /**
   * Whether to print which structure the entry belongs to.
   *
   * Off on a record, which is already about one structure, and on in the review
   * queue, which spans a department. The photograph's URL is NOT taken from
   * this — it comes off the event's own `bridgeId`, so a row cannot be drawn
   * with one structure's name and another's photograph.
   */
  showBridge?: boolean;
  /** A move the register refused, for the row it was made on to say why. */
  refused?: State['refused'];
}

export function progressRow(e: ProgressEntry, o: RowOptions): HTMLElement {
  return h(
    'tr',
    /* An entry still on this device reads differently from one the register
       holds, and it has to: somebody who cannot tell them apart will record the
       same pour twice when the first one appears not to have saved. */
    { class: [e.pending && 'is-muted'] },

    h('td', { style: 'padding-left:14px' }, h('span', { text: activityLabel(e.activity) })),

    o.showBridge ? h('td', { class: 'display', text: e.bridgeId }) : null,

    h('td', { text: progressWhere(e) }),

    /* Right-aligned with the other numbers on this screen, and EMPTY rather
       than zero where nothing was measured — a blank cell says nobody measured
       it and a `0` says somebody measured nothing. */
    h('td', { class: 'num', text: progressQuantity(e) }),

    h('td', { class: 'num', text: prettyDate(e.occurredOn) }),

    h(
      'td',
      {},
      e.pending
        ? /* No state picker on something the register has never seen. The row
             exists so the person who recorded it can see that it landed on the
             device; there is nothing to review until it has been sent. */
          tag('On this device', 2, 'Recorded here and not yet sent to the register.')
        : o.mayReview
          ? h(
              'select',
              {
                class: 'select select--sm',
                'aria-label': `State of ${activityLabel(e.activity)} on ${e.occurredOn}`,
                onchange: revertOnRefusal(e.state, (v) => actions.setProgressEntryState(e.id, v as ProgressState)),
              },
              ...STATES.map((st) =>
                h('option', { value: st, selected: st === e.state, text: STATE_LABEL[st] }),
              ),
              /* A stored state this build does not know is offered as itself so
                 the picker shows what is actually stored rather than silently
                 displaying the first option. */
              (STATES as readonly string[]).includes(e.state)
                ? null
                : h('option', { value: e.state, selected: true, text: e.state }),
            )
          : progressStateTag(e.state),
      o.mayReview ? refusalNote(o.refused ?? null, e.id) : null,
    ),

    h(
      'td',
      {},
      e.hasPhoto
        ? h('img', {
            class: 'works__thumb',
            /* TEMPORARY — the FWO demonstration; delete the `??` branch with
               src/data/fwo-demo.ts. A register entry's photograph is fetched by
               id from the bucket, which is what `hasPhoto` means; a fabricated
               one has no row to fetch and carries its own drawing instead. The
               alternative was giving those rows `hasPhoto: false`, which would
               have demonstrated the card by hiding the feature it is meant to
               show. */
            src: e.photoUrl ?? api.progressPhotoUrl(e.bridgeId, e.id),
            loading: 'lazy',
            /* And it does not call itself a photograph unless it is one: the
               demonstration's picture is a drawing. */
            alt: e.photoUrl
              ? `Drawing of ${activityLabel(e.activity)}`
              : `Photograph of ${activityLabel(e.activity)} on ${e.occurredOn}`,
          })
        : null,
    ),
  );
}

/* --------------------------- the totals --------------------------- */

/**
 * One line per activity and unit, and a count of what has not been checked.
 *
 * Printed above the rows rather than below them because it is the answer
 * somebody opens the card for; the rows are the evidence for it.
 */
export function progressTotals(summary: ProgressSummary): HTMLElement | null {
  const lines = [...summary.quantities].sort(
    (a, b) => activityLabel(a.activity).localeCompare(activityLabel(b.activity)) || a.unit.localeCompare(b.unit),
  );
  if (summary.count === 0) return null;

  return h(
    'div',
    { class: 'works__totals' },
    h('span', {
      class: 'works__stat',
      text: `${summary.count} ${summary.count === 1 ? 'entry' : 'entries'}`,
    }),
    summary.draft > 0
      ? h('span', {
          class: 'works__stat works__stat--draft',
          text: `${summary.draft} not yet verified`,
        })
      : null,
    ...lines.map((line) =>
      h('span', {
        class: 'works__stat',
        /* The unit is part of the figure and never dropped. A bare "412" on a
           report is a number nobody can use, and "412 m³" is not much better
           until it says of what. */
        text: `${activityLabel(line.activity)} ${trimNumber(line.total)} ${line.unit}`,
      }),
    ),
  );
}

/* --------------------------- the card --------------------------- */

/**
 * The Works card: one structure's site record.
 *
 * FOUR STATES, and the difference between the middle two is the point of the
 * whole card:
 *
 *   · the register could not be reached — say so, and do not claim anything
 *     about the structure;
 *   · it was read and holds nothing — nothing has been recorded here;
 *   · it was read and holds entries — draw them;
 *   · this device is on the bundled seed and there is no record to read at all.
 *
 * An empty list drawn as "No work recorded" after a failed request would be the
 * register stating a fact about a bridge on the strength of a timeout. See
 * `progressRead` in src/core/store.ts.
 */
export function worksBlock(bridgeId: string, bridgeName: string, s: Readonly<State>): HTMLElement {
  const mayRecord = can(s, 'progress.record');
  const mayReview = can(s, 'progress.review');

  return h(
    'div',
    { class: 'det-block det-block--flush' },
    cardHead(
      'Works',
      s.progressRead === 'read' && s.progressSummary.count > 0
        ? `${s.progressSummary.count} recorded`
        : undefined,
      /* FOUR BUTTONS, and they are the four things somebody on a site does.
         Withheld rather than removed for a role that may not record, the rule
         the field actions keep: an absent button reads as a broken
         application and a disabled one carrying the reason reads as an
         answer. */
      button('Record work', {
        sm: true,
        iconPath: ICONS.plus,
        disabled: !mayRecord,
        title: mayRecord
          ? `Record something done on ${bridgeName}`
          : whyNot(s, 'Recording work', 'progress.record'),
        onClick: () => actions.openRecordProgress(bridgeId),
      }),
      button('Daily return', {
        sm: true,
        disabled: !mayRecord,
        title: mayRecord
          ? `Record the weather, the labour and the plant on ${bridgeName} for a day`
          : whyNot(s, 'Recording a daily return', 'progress.record'),
        onClick: () => actions.openSiteReturn(bridgeId),
      }),
      button('Incident', {
        sm: true,
        disabled: !mayRecord,
        title: mayRecord
          ? 'Report a near miss, an injury, a dangerous occurrence or an environmental event'
          : whyNot(s, 'Reporting an incident', 'progress.record'),
        onClick: () => actions.openIncident(bridgeId),
      }),
      button('Document', {
        sm: true,
        disabled: !mayRecord,
        title: mayRecord
          ? 'File a test certificate, a delivery note or a method statement'
          : whyNot(s, 'Filing a document', 'progress.record'),
        onClick: () => actions.openDocument(bridgeId),
      }),
      button('Test', {
        sm: true,
        disabled: !mayRecord,
        title: mayRecord
          ? 'Record a cube, a pile test or a strand or rebar certificate'
          : whyNot(s, 'Recording a test result', 'progress.record'),
        onClick: () => actions.openTest(bridgeId),
      }),
      button('Raise', {
        sm: true,
        disabled: !mayRaiseAny(s),
        title: mayRaiseAny(s)
          ? 'Raise a hold point, a non-conformance or a request for information'
          : whyNot(s, 'Raising a hold point or an RFI', 'progress.record'),
        onClick: () => actions.openItem(bridgeId),
      }),
    ),
    worksBody(s, mayReview),
    /* The three records that are not progress, under the entries and in the
       order a report reads them. Each draws nothing at all when it holds
       nothing — unlike the report, where an empty section is the point. On a
       record the reader is looking at one structure and already knows the
       Works card is there; four empty tables under it would be noise. */
    siteDiary(s),
    incidentList(s),
    documentList(s, bridgeId),
    testList(s),
    itemList(s),
  );
}

/**
 * The work recorded on a structure, alone — for the content panel's Works page
 * in Construction Monitoring, where the daily returns, the incidents and the
 * documents are pages of their own and the ribbon carries the four buttons.
 * The table only: the panel names it, and the totals are the period report's.
 */
export function worksEntries(s: Readonly<State>): Child {
  return worksBody(s, can(s, 'progress.review'), false);
}

function worksBody(s: Readonly<State>, mayReview: boolean, totals = true): Child {
  if (s.progressRead === 'unreachable') {
    return emptyState(
      'The construction record could not be read. This says nothing about what has been ' +
        'built — only that the register did not answer.',
    );
  }
  if (s.progressRead === 'unknown') {
    return emptyState(
      'This device is reading the bundled register rather than the live one, which carries ' +
        'no construction record.',
    );
  }
  if (s.progress.length === 0) {
    return emptyState('No work has been recorded against this structure.');
  }

  return h(
    'div',
    {},
    totals ? progressTotals(s.progressSummary) : null,
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'Activity' }),
          h('th', { text: 'Where' }),
          h('th', { class: 'num', text: 'Quantity' }),
          h('th', { class: 'num', text: 'Done on' }),
          h('th', { text: 'State' }),
          h('th', { text: '' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...s.progress.map((e) => progressRow(e, { mayReview, refused: s.refused })),
      ),
    ),
  );
}

/* --------------------------- the diary --------------------------- */

/**
 * The daily returns on this structure, newest day first.
 *
 * DRAWN ONLY WHERE THERE ARE ANY, unlike the report's sections. The report is
 * a document read against a contract's list of required contents, so an empty
 * section there is the most useful thing on the page; a record is read by
 * somebody looking at one bridge, and four empty tables under the Works card
 * would be noise they scroll past.
 */
export function siteDiary(s: Readonly<State>, headed = true): Child {
  if (s.progressRead !== 'read' || s.siteReturns.length === 0) return null;

  const mayReview = can(s, 'progress.review');

  return h(
    'div',
    { class: [headed && 'works__sub'] },
    headed ? h('div', { class: 'works__sub-head', text: 'Daily returns' }) : null,
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'Day' }),
          h('th', { text: 'Weather' }),
          h('th', { class: 'num', text: 'On site' }),
          h('th', { text: 'Plant' }),
          h('th', { class: 'num', text: 'Rain' }),
          h('th', { text: 'State' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...s.siteReturns.map((r) =>
          h(
            'tr',
            {},
            h('td', { style: 'padding-left:14px', text: prettyDate(r.returnedOn) }),
            /* Its sky, and marked rather than merely named where it is weather
               that ordinarily stops work — what a reader scans this column for. */
            h('td', {}, weatherChip(r.weather)),
            /* EMPTY IS "NOBODY COUNTED" AND 0 IS "NOBODY WAS HERE", and the two
               have to look different: an idle day is the evidence behind a
               claim for more time and a day nobody counted is not evidence of
               anything. */
            h('td', {
              class: 'num',
              text: r.labour === null ? '—' : String(r.labour),
              /* By trade where the site counted that way (LC-56). */
              title: r.labour === null ? 'Nobody counted' : (r.labourByTrade ?? []).length ? tradesText(r) : `${r.labour} on site`,
            }),
            h(
              'td',
              {},
              h('span', { text: plantText(r) }),
              r.hasPhoto && !r.pending ? photoLink(api.sitePhotoUrl(r.bridgeId, 'return', r.id)) : null,
            ),
            h('td', { class: 'num', text: r.rainfallMm == null ? '—' : `${trimNumber(r.rainfallMm)} mm` }),
            h(
              'td',
              {},
              mayReview
                ? h(
                    'select',
                    {
                      class: 'select select--sm',
                      'aria-label': `State of the return for ${r.returnedOn}`,
                      onchange: revertOnRefusal(r.state, (v) => actions.setReturnEntryState(r.id, v as ProgressState)),
                    },
                    ...STATES.map((st) =>
                      h('option', { value: st, selected: st === r.state, text: STATE_LABEL[st] }),
                    ),
                  )
                : progressStateTag(r.state),
              mayReview ? refusalNote(s.refused, r.id) : null,
            ),
          ),
        ),
      ),
    ),
  );
}

/** A photograph on a return or an incident, opened on its own (LC-56). */
function photoLink(href: string): HTMLElement {
  return h('a', { class: 'works__photo', href, target: '_blank', rel: 'noopener', text: ' · photograph' });
}

/* --------------------------- incidents --------------------------- */

export function incidentList(s: Readonly<State>, headed = true): Child {
  if (s.progressRead !== 'read' || s.incidents.length === 0) return null;
  /* CLOSING IS THE RESIDENT ENGINEER'S (LC-50), and the endpoint refuses anybody
     else — so the control is drawn only for somebody who holds it. */
  const maySupervise = can(s, 'works.supervise');
  const open = s.incidents.filter((i) => !i.closed).length;

  return h(
    'div',
    { class: [headed && 'works__sub'] },
    headed ? h('div', { class: 'works__sub-head', text: 'Safety and environmental' }) : null,
    h(
      'div',
      { class: 'works__totals' },
      h('span', {
        class: open > 0 ? 'works__stat works__stat--draft' : 'works__stat',
        'data-open': String(open),
        text: `${open} open`,
      }),
      h('span', { class: 'works__stat', text: `${s.incidents.length - open} closed` }),
    ),
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'What' }),
          h('th', { class: 'num', text: 'When' }),
          h('th', { text: 'Description' }),
          h('th', { text: 'Action' }),
          maySupervise ? h('th', { text: '' }) : null,
        ),
      ),
      h(
        'tbody',
        {},
        ...s.incidents.flatMap((i) => {
          const closing = s.incidentClose?.id === i.id ? s.incidentClose : null;
          const row = h(
            'tr',
            { class: [i.closed && 'is-muted'] },
            h(
              'td',
              { style: 'padding-left:14px' },
              /* A lost-time injury is the one a client's eye goes to, so it is
                 the one the ramp marks loudest. */
              tag(incidentLabel(i.kind), isLostTime(i.kind) ? 3 : 2),
            ),
            h('td', { class: 'num', text: prettyDate(i.occurredOn) }),
            h(
              'td',
              {},
              h('span', { text: i.description }),
              /* Who it involved and the days it cost (LC-56), where recorded. */
              i.peopleInvolved != null || i.daysLost != null
                ? h('span', {
                    class: 'muted',
                    'data-involved': `${i.peopleInvolved ?? ''}|${i.daysLost ?? ''}`,
                    text: [
                      i.peopleInvolved != null ? ` · ${i.peopleInvolved} involved${i.peopleNames ? ` (${i.peopleNames})` : ''}` : '',
                      i.daysLost != null ? ` · ${trimNumber(i.daysLost)} days lost` : '',
                    ].join(''),
                  })
                : null,
              i.hasPhoto && !i.pending ? photoLink(api.sitePhotoUrl(i.bridgeId, 'incident', i.id)) : null,
            ),
            h(
              'td',
              {},
              i.closed
                ? h(
                    'span',
                    {},
                    h('span', { text: i.action || 'Closed' }),
                    i.closedOn
                      ? h('span', {
                          class: 'muted',
                          text: ` · closed ${prettyDate(i.closedOn)}${i.closedBy ? ` by ${i.closedBy}` : ''}`,
                        })
                      : null,
                  )
                : h(
                    'span',
                    {},
                    /* AN OPEN ITEM IS PRINTED AS OPEN, with or without an
                       account of what is being done. An incident that closed by
                       being forgotten is the failure this column exists to make
                       visible. */
                    h('span', { class: 'works__stat works__stat--draft', text: 'Open' }),
                    i.action ? h('span', { text: ` ${i.action}` }) : null,
                  ),
            ),
            maySupervise
              ? h(
                  'td',
                  {},
                  !i.closed && !closing && !i.pending
                    ? button('Close', {
                        sm: true,
                        title: 'Close this incident out, with what was done and the day',
                        onClick: () => actions.openIncidentClose(i.id),
                      })
                    : null,
                )
              : null,
          );
          return closing ? [row, closeOutRow(s, closing)] : [row];
        }),
      ),
    ),
  );
}

/** The close-out of one incident, under its row: what was done, and the day. */
function closeOutRow(
  s: Readonly<State>,
  c: { id: string; action: string; closedOn: string },
): HTMLElement {
  return h(
    'tr',
    { class: 'works__close' },
    h(
      'td',
      { colspan: 5, style: 'padding-left:14px' },
      h(
        'div',
        { class: 'works__close-form' },
        h('textarea', {
          id: 'inc-close-action',
          class: 'input',
          rows: 2,
          'aria-label': 'Close-out note',
          placeholder: 'What was done, and why it is finished',
          value: c.action,
          oninput: (e: Event) =>
            actions.setIncidentCloseField('action', (e.target as HTMLTextAreaElement).value),
        }),
        h('input', {
          id: 'inc-close-date',
          class: 'input input--sm',
          type: 'date',
          'aria-label': 'Closed out on',
          value: c.closedOn,
          oninput: (e: Event) =>
            actions.setIncidentCloseField('closedOn', (e.target as HTMLInputElement).value),
        }),
        button('Close incident', {
          sm: true,
          primary: true,
          onClick: () => void actions.submitIncidentClose(),
        }),
        button('Cancel', { sm: true, onClick: () => actions.cancelIncidentClose() }),
        s.incidentError ? h('span', { class: 'dlg__note dlg__note--error', text: s.incidentError }) : null,
      ),
    ),
  );
}

/* --------------------------- documents --------------------------- */

export function documentList(s: Readonly<State>, bridgeId: string, headed = true): Child {
  if (s.progressRead !== 'read' || s.documents.length === 0) return null;

  return h(
    'div',
    { class: [headed && 'works__sub'] },
    headed ? h('div', { class: 'works__sub-head', text: 'Documents' }) : null,
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'Title' }),
          h('th', { text: 'Kind' }),
          h('th', { class: 'num', text: 'Dated' }),
          h('th', { class: 'num', text: 'Size' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...s.documents.map((doc) =>
          h(
            'tr',
            {},
            h(
              'td',
              { style: 'padding-left:14px' },
              /* A link and not a button: it is a different document and
                 opening it is what should happen. `target` so a certificate
                 opened to check one figure does not lose the record behind it. */
              h(
                'a',
                /* TEMPORARY — the FWO demonstration; delete the `fileUrl`
                   branch with src/data/fwo-demo.ts. A fabricated document
                   carries its own file as a data: URL, which a browser will
                   download but will not open in a new tab. */
                doc.fileUrl
                  ? { href: doc.fileUrl, download: doc.filename, title: doc.note || doc.title }
                  : {
                      href: api.documentUrl(bridgeId, doc.id),
                      target: '_blank',
                      rel: 'noopener',
                      title: doc.note || doc.title,
                    },
                doc.title,
              ),
            ),
            h('td', { text: documentKindLabel(doc.kind) }),
            /* A document with no date on it says so rather than borrowing the
               day it was filed — see the column's note in migration 0031. */
            h('td', { class: 'num', text: doc.datedOn ? prettyDate(doc.datedOn) : '—' }),
            h('td', { class: 'num', text: `${Math.round(doc.bytes / 1024)} kB` }),
          ),
        ),
      ),
    ),
  );
}
